import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, finalize, Observable, take } from 'rxjs';
import {
  GroupEdwSummaryReport,
  GroupEdwSummaryRequest,
  GroupForecastReport,
  GroupForecastRequest,
  LocationEdwSummaryReport,
  LocationEdwSummaryRequest,
  LocationForecastReport,
  LocationForecastRequest,
} from 'src/app/core/models/reports/reports.model';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { environment } from 'src/environments/environment';
import { ErrorhandleService } from '../../error-handling/error-handle/errorhandle.service';

@Injectable({
  providedIn: 'root',
})
export class IdoReportsService {
  baseUrl = environment.apiUrl.concat('report-management/');
  constructor(
    private http: HttpClient,
    private commonUtils: CommonUtils,
    private errorHandler: ErrorhandleService,
  ) {}

  /**
   *Get a specific Location Forecast report based on the passed in location code
   *
   * @param {string} locationCode
   * @return {*}  {Observable<LocationForecastRequest>}
   * @memberof IdoReportsService
   */
  getLocationForecastReport(
    request: LocationForecastRequest,
  ): Observable<LocationForecastReport> {
    this.commonUtils.updateLoadingModal(true);

    return this.http
      .get<LocationForecastReport>(
        this.baseUrl.concat('Location-Forecast-vs-Actual-report'),
        {
          params: { ...request },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<LocationForecastReport>(
            error,
            error.status,
            'IDOReports Service: Get LocationForecast report',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *Get a specific Group Forecast report based on the passed in location code
   *
   * @param {string} locationCode
   * @return {*}  {Observable<GroupForecastRequest>}
   * @memberof IdoReportsService
   */
  getGroupForecastReport(
    request: GroupForecastRequest,
  ): Observable<GroupForecastReport> {
    this.commonUtils.updateLoadingModal(true);

    return this.http
      .get<GroupForecastReport>(
        this.baseUrl.concat('Group-Forecast-vs-Actual-report'),
        {
          params: { ...request },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<GroupForecastReport>(
            error,
            error.status,
            'IDOReports Service: Get GroupForecast report',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *Get a specific Location EDW Summary report based on the passed in location code
   *
   * @param {string} locationCode
   * @return {*}  {Observable<LocationEdwSummaryReport>}
   * @memberof IdoReportsService
   */
  getLocationEdwSummaryReport(
    request: LocationEdwSummaryRequest,
  ): Observable<LocationEdwSummaryReport> {
    this.commonUtils.updateLoadingModal(true);

    return this.http
      .get<LocationEdwSummaryReport>(
        this.baseUrl.concat('location-EDW-summary'),
        {
          params: { ...request },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<LocationEdwSummaryReport>(
            error,
            error.status,
            'IDOReports Service: Get LocationEdwSummary report',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *Get a specific Group EDW Summary report based on the passed in location code
   *
   * @param {GroupEdwSummaryRequest} request
   * @return {*}  {Observable<GroupEdwSummaryReport>}
   * @memberof IdoReportsService
   */
  getGroupEdwSummaryReport(
    request: GroupEdwSummaryRequest,
    locationCode: string,
  ): Observable<GroupEdwSummaryReport> {
    this.commonUtils.updateLoadingModal(true);

    return this.http
      .get<GroupEdwSummaryReport>(this.baseUrl.concat('group-EDW-summary'), {
        params: { ...request },
        headers: {
          locationCode,
        },
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<GroupEdwSummaryReport>(
            error,
            error.status,
            'IDOReports Service: Get GroupEdwSummary report',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }
}
